import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import {
  postCreateComicList,
  postCreateReleaseList,
  postAddComicToLists,
  postAddReleaseToLists,
  postRemoveComicFromList,
  postRemoveReleaseFromList,
  CreateListPayload,
  AddToListsPayload,
} from "../api/lists";

interface UseListMutationsOptions {
  entityType: "comic" | "release";
  entityId: number;
  onCreateSuccess?: () => void;
  onAddSuccess?: () => void;
  onRemoveSuccess?: () => void;
  onClose?: () => void;
  addCurrentEntityToNewList?: boolean;
}

export function useListMutations({
  entityType,
  entityId,
  onCreateSuccess,
  onAddSuccess,
  onRemoveSuccess,
  onClose,
  addCurrentEntityToNewList = false,
}: UseListMutationsOptions) {
  const toast = useToast();
  const queryClient = useQueryClient();

  // Create list mutation
  const createListMutation = useMutation({
    mutationFn: (payload: CreateListPayload) => {
      return entityType === "comic"
        ? postCreateComicList(payload)
        : postCreateReleaseList(payload);
    },
    onSuccess: async (response) => {
      queryClient.invalidateQueries({ queryKey: [`user-${entityType}-lists`] });
      
      // Also refetch immediately to ensure fresh data
      queryClient.refetchQueries({ queryKey: [`user-${entityType}-lists`] });
      
      const newListId = response.data.payload.results.id;
      
      // If flag is set, automatically add current entity to the new list
      if (addCurrentEntityToNewList && newListId) {
        try {
          await (entityType === "comic"
            ? postAddComicToLists(entityId, { list_ids: [newListId] })
            : postAddReleaseToLists(entityId, { list_ids: [newListId] }));
          
          // Refresh again after adding entity
          queryClient.invalidateQueries({ queryKey: [`user-${entityType}-lists`] });
          queryClient.refetchQueries({ queryKey: [`user-${entityType}-lists`] });
          
          toast({
            title: "List created and entity added",
            description: `"${response.data.payload.results.display_name}" has been created and the ${entityType} was added to it.`,
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } catch (addError: any) {
          // Still show success for list creation, but indicate add failed
          toast({
            title: "List created but failed to add entity",
            description: `"${response.data.payload.results.display_name}" was created, but the ${entityType} couldn't be added automatically.`,
            status: "warning",
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        toast({
          title: "List created successfully",
          description: `"${response.data.payload.results.display_name}" has been created.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
      
      onCreateSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create list",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Add to lists mutation
  const addToListsMutation = useMutation({
    mutationFn: (payload: AddToListsPayload) => {
      if (entityType === "comic") {
        return postAddComicToLists(entityId, payload);
      } else {
        return postAddReleaseToLists(entityId, payload);
      }
    },
    onSuccess: (response) => {
      const results = response.data.payload.results;
      const successCount = results.filter((r) => r.success).length;
      const failCount = results.filter((r) => !r.success).length;

      if (successCount > 0) {
        toast({
          title: `Added to ${successCount} list${successCount === 1 ? "" : "s"}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      if (failCount > 0) {
        const failedResults = results.filter((r) => !r.success);
        toast({
          title: `Failed to add to ${failCount} list${failCount === 1 ? "" : "s"}`,
          description: failedResults[0]?.message || "Some additions failed",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }

      // Refresh the lists to get updated state
      queryClient.invalidateQueries({ queryKey: [`user-${entityType}-lists`] });
      
      // Also refetch immediately to ensure fresh data
      queryClient.refetchQueries({ queryKey: [`user-${entityType}-lists`] });
      
      onAddSuccess?.();
      onClose?.();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to add to lists",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // Remove from list mutation
  const removeFromListMutation = useMutation({
    mutationFn: (listId: number) => {
      if (entityType === "comic") {
        return postRemoveComicFromList(entityId, listId);
      } else {
        return postRemoveReleaseFromList(entityId, listId);
      }
    },
    onSuccess: () => {
      toast({
        title: "Removed from list",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Refresh the lists to get updated state
      queryClient.invalidateQueries({ queryKey: [`user-${entityType}-lists`] });
      
      // Also refetch immediately to ensure fresh data
      queryClient.refetchQueries({ queryKey: [`user-${entityType}-lists`] });
      
      onRemoveSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Failed to remove from list",
        description: error.response?.data?.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const isLoading =
    createListMutation.isPending ||
    addToListsMutation.isPending ||
    removeFromListMutation.isPending;

  return {
    createListMutation,
    addToListsMutation,
    removeFromListMutation,
    isLoading,
  };
}